<template>
  <md-dialog
    :md-active.sync="isOpened"
    :md-click-outside-to-close="false"
    :md-close-on-esc="false"
    class="inactivity-message"
  >
    <div class="inactivity-message__popup">
      <h2>{{ i18n.inm_session_timeout() }}</h2>
      <p>{{ i18n.inm_session_timeout_descr() }}</p>

      <div class="inactivity-message__buttons">
        <md-button class="btn md-primary" @click="cancel">
          {{ i18n.lbl_cancel() }}
        </md-button>

        <md-button class="btn md-primary" @click="logout">
          {{ i18n.lbl_logout() }} ({{ leftToLogout }})
        </md-button>
      </div>
    </div>
  </md-dialog>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { i18n } from '@js/i18n'
  const AUTO_LOGOUT_MINUTES_KEY = 'auto_logout_minutes'
  const defaultSessionTimeoutMinutes = 30
  const popupBeforeLogoutSeconds = 30
  const tickIntervalMs = 1000

  export default {
    name: 'inactivity-message',

    data () {
      return {
        forceLogoutAt: null,
        showPopupAt: null,
        leftToLogout: null,
        lastLoggedLeftToLogout: null,
        logoutIntervalID: null,
        listenersAttached: false,
        isOpened: false,
        i18n
      }
    },

    computed: {
      ...mapGetters({
        isLoggedIn: vuexTypes.isLoggedIn
      })
    },

    beforeDestroy () {
      this.stop()
    },

    watch: {
      isLoggedIn: {
        immediate: true,
        handler (val) {
          if (val) {
            this.start()
          } else {
            this.stop()
          }
        }
      }
    },

    methods: {
      ...mapActions([
        vuexTypes.LOG_OUT
      ]),
      attachListeners () {
        if (this.listenersAttached) {
          return
        }
        document.addEventListener('mousemove', this.handleUserActivity)
        document.addEventListener('mousedown', this.handleUserActivity)
        document.addEventListener('touchstart', this.handleUserActivity)
        document.addEventListener('click', this.handleUserActivity)
        document.addEventListener('keypress', this.handleUserActivity)
        document.addEventListener('keydown', this.handleUserActivity)
        window.addEventListener('auto-logout-timeout-updated', this.onAutoLogoutTimeoutUpdated)
        this.listenersAttached = true
      },
      destroyListeners () {
        if (!this.listenersAttached) {
          return
        }
        document.removeEventListener('mousemove', this.handleUserActivity)
        document.removeEventListener('mousedown', this.handleUserActivity)
        document.removeEventListener('touchstart', this.handleUserActivity)
        document.removeEventListener('click', this.handleUserActivity)
        document.removeEventListener('keypress', this.handleUserActivity)
        document.removeEventListener('keydown', this.handleUserActivity)
        window.removeEventListener('auto-logout-timeout-updated', this.onAutoLogoutTimeoutUpdated)
        this.listenersAttached = false
      },
      getAutoLogoutSettings () {
        const rawMinutes = Number(sessionStorage.getItem(AUTO_LOGOUT_MINUTES_KEY))
        if (Number.isFinite(rawMinutes) && rawMinutes === 0) {
          // console.log('[auto-logout][inactivity] disabled by sessionStorage value 0')
          return {
            enabled: false,
            timeoutMinutes: 0
          }
        }
        if (Number.isFinite(rawMinutes) && rawMinutes > 0) {
          // console.log('[auto-logout][inactivity] timeout from sessionStorage:', rawMinutes)
          return {
            enabled: true,
            timeoutMinutes: rawMinutes
          }
        }
        // console.log('[auto-logout][inactivity] timeout fallback to default:', defaultSessionTimeoutMinutes)
        return {
          enabled: true,
          timeoutMinutes: defaultSessionTimeoutMinutes
        }
      },
      start () {
        if (this.logoutIntervalID) {
          return
        }
        this.attachListeners()
        const { enabled } = this.getAutoLogoutSettings()
        if (!enabled) {
          this.disableAutoLogout()
          return
        }
        // console.log('[auto-logout][inactivity] start timer')
        this.reset()
        this.logoutIntervalID = window.setInterval(this.tick, tickIntervalMs)
      },
      stop () {
        // console.log('[auto-logout][inactivity] stop timer')
        this.isOpened = false
        this.removeInterval()
        this.destroyListeners()
        this.forceLogoutAt = null
        this.showPopupAt = null
        this.leftToLogout = null
        this.lastLoggedLeftToLogout = null
      },
      removeInterval () {
        if (!this.logoutIntervalID) {
          return
        }
        window.clearInterval(this.logoutIntervalID)
        this.logoutIntervalID = null
      },
      disableAutoLogout () {
        this.isOpened = false
        this.removeInterval()
        this.forceLogoutAt = null
        this.showPopupAt = null
        this.leftToLogout = null
        this.lastLoggedLeftToLogout = null
      },
      reset () {
        if (!this.isLoggedIn) {
          return
        }
        const { enabled, timeoutMinutes } = this.getAutoLogoutSettings()
        if (!enabled) {
          this.disableAutoLogout()
          return
        }
        const timeoutSeconds = timeoutMinutes * 60
        const popupSeconds = Math.min(popupBeforeLogoutSeconds, timeoutSeconds)
        const now = Date.now()
        this.forceLogoutAt = now + timeoutSeconds * 1000
        this.showPopupAt = this.forceLogoutAt - popupSeconds * 1000
        this.leftToLogout = Math.max(0, Math.ceil((this.forceLogoutAt - now) / 1000))
        this.lastLoggedLeftToLogout = null
        this.isOpened = false
        // console.log('[auto-logout][inactivity] reset timer', {
        //   timeoutMinutes,
        //   forceLogoutAt: new Date(this.forceLogoutAt).toISOString(),
        //   showPopupAt: new Date(this.showPopupAt).toISOString(),
        //   leftToLogout: this.leftToLogout
        // })
      },
      handleUserActivity (event) {
        if (!this.isLoggedIn) {
          return
        }
        if (event && event.isTrusted === false) {
          return
        }
        if (this.isOpened) {
          return
        }
        // console.log('[auto-logout][inactivity] reset by user activity')
        this.reset()
      },
      onAutoLogoutTimeoutUpdated () {
        if (!this.isLoggedIn) {
          return
        }
        // console.log('[auto-logout][inactivity] timeout updated event received')
        const { enabled } = this.getAutoLogoutSettings()
        if (!enabled) {
          this.disableAutoLogout()
          return
        }
        if (!this.logoutIntervalID) {
          this.start()
          return
        }
        this.reset()
      },
      cancel () {
        // console.log('[auto-logout][inactivity] cancel clicked, reset timer')
        this.reset()
      },
      logout () {
        // console.log('[auto-logout][inactivity] trigger LOG_OUT')
        this.stop()
        this.LOG_OUT()
      },
      tick () {
        if (!this.isLoggedIn) {
          this.stop()
          return
        }
        if (!this.forceLogoutAt || !this.showPopupAt) {
          this.reset()
        }
        const now = Date.now()
        const leftToLogout = Math.max(0, Math.ceil((this.forceLogoutAt - now) / 1000))
        this.leftToLogout = leftToLogout
        if (leftToLogout <= 10 || leftToLogout % 30 === 0) {
          if (this.lastLoggedLeftToLogout !== leftToLogout) {
            // console.log('[auto-logout][inactivity] tick leftToLogout:', leftToLogout)
            this.lastLoggedLeftToLogout = leftToLogout
          }
        }
        if (now >= this.forceLogoutAt) {
          this.logout()
          return
        }
        if (now >= this.showPopupAt) {
          if (!this.isOpened) {
            // console.log('[auto-logout][inactivity] open timeout modal')
          }
          this.isOpened = true
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '~scss/variables';
  @import '~scss/mixins';

  .inactivity-message {
    min-width: 550px;
    max-width: 550px;

    @include respond-to($small) {
      min-width: 95%;
      max-width: 95%;
    }
  }

  .inactivity-message__popup {
    h2 {
      margin: 0 0 12px;
    }

    p {
      margin: 0 0 22px;
    }
  }

  .inactivity-message__buttons {
    display: flex;
    justify-content: flex-end;

    button {
      flex: .25;

      &:first-child {
        margin-right: 10px;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/vue/common/messages/InactivityMessage.vue